import { type FC, useState } from 'react';
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup,
  SidebarGroupContent, SidebarGroupLabel, SidebarInset,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarProvider, SidebarTrigger,
} from '../ui/sidebar';
import {
  Home, Shield, Users, UserCog, Settings, ArrowLeft,
  FileText, MessageCircle, Link2,
  LayoutDashboard, ListOrdered, Clock, AlertCircle, PieChart, Wallet,
  Calculator, FolderTree,
} from 'lucide-react';
import { Separator } from '../ui/separator';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { Button } from '../ui/button';
import { NavUser } from './NavUser';
import { AppLogo } from '../Icons/AppLogo';
import { CONTABILIDAD_ROUTES } from '@/contabilidad/constants/routes';
import { PRESUPUESTOS_ROUTES } from '@/presupuestos/constants/routes';

const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_STATE_KEY = "sidebar_open";

export interface SidebarLayoutProps {
  children: React.ReactNode;
  prevButtonVisible?: boolean;
  onPrevClick?: () => void;
  title?: string;
}

const SidebarLayout: FC<SidebarLayoutProps> = ({
  children,
  title = "",
  prevButtonVisible = true,
  onPrevClick,
}) => {
  const { user } = useAuth();
  const { canManageUsers, canManageAuthorization, hasAnyPermissions, canAccessContabilidad } = useAuthorization();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  const inContabilidad = location.pathname.startsWith('/contabilidad');

  const handleOpenChange = (open: boolean) => {
    setSidebarOpen(open);
    localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(open));
  };

  const isActive = (url: string, exact = true) =>
    exact ? location.pathname === url : location.pathname.startsWith(url);

  const activeClass = 'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground';

  const items = [
    { title: "Escrituras",              url: "/",                     icon: Home,        show: true },
    { title: "Otros Trámites",          url: "/otros-tramites",       icon: FileText,    show: hasAnyPermissions(['MANAGE_TRAMITES']) },
    { title: "Presupuestos",            url: PRESUPUESTOS_ROUTES.LISTADO, icon: Calculator, show: hasAnyPermissions(['VIEW_PRESUPUESTOS_PROFORMA', 'CREATE_PRESUPUESTOS_PROFORMA']) },
    { title: "Gestión de usuarios",     url: "/usuarios",             icon: Users,       show: canManageUsers },
    { title: "Grupo de personas",       url: "/grupo-personas",       icon: UserCog,     show: canManageUsers },
    { title: "Roles y permisos",        url: "/roles-permisos",       icon: Shield,      show: canManageAuthorization },
    { title: "Configuración del Sistema", url: "/configuracion-sistema", icon: Settings, show: hasAnyPermissions(['MANAGE_SYSTEM_CONFIGURATION']) },
    { title: "Cola WhatsApp",           url: "/whatsapp-cola",        icon: MessageCircle, show: hasAnyPermissions(['SEND_WHATSAPP', 'VIEW_WHATSAPP_DISPATCHES']) },
    { title: "Enlaces temporales",      url: "/enlaces-temporales",   icon: Link2,       show: hasAnyPermissions(['VIEW_TEMPORARY_LINKS']) },
  ].filter(item => item.show !== false);

  return (
    <SidebarProvider
      style={{ '--sidebar-width': SIDEBAR_WIDTH, '--sidebar-width-mobile': SIDEBAR_WIDTH_MOBILE } as React.CSSProperties}
      open={sidebarOpen}
      onOpenChange={handleOpenChange}
    >
      <Sidebar collapsible='icon'>
        <SidebarContent>
          {/* ── Navegación general ── */}
          <SidebarGroup>
            <div className="p-4 flex justify-center items-center border-b border-sidebar-border group-data-[collapsible=icon]:hidden">
              <Link to="/" className="flex-shrink-0">
                <AppLogo height={45} width={180} />
              </Link>
            </div>
            <SidebarGroupLabel>Navegación</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map((item) => {
                  const active = isActive(item.url);
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title} isActive={active} className={active ? activeClass : ''}>
                        <Link to={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* ── Contabilidad ── */}
          {canAccessContabilidad && (
            <SidebarGroup>
              <SidebarGroupLabel>Contabilidad</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {[
                    { title: 'Resumen',             url: CONTABILIDAD_ROUTES.RESUMEN,           icon: LayoutDashboard, tooltip: 'Resumen Financiero'        },
                    { title: 'Movimientos',          url: CONTABILIDAD_ROUTES.MOVIMIENTOS,        icon: ListOrdered,     tooltip: 'Historial de Movimientos'  },
                    { title: 'Cobros Pendientes',    url: CONTABILIDAD_ROUTES.COBROS_PENDIENTES,  icon: Clock,           tooltip: 'Cobros Pendientes'         },
                    { title: 'Egresos Pendientes',   url: CONTABILIDAD_ROUTES.EGRESOS_PENDIENTES, icon: AlertCircle,     tooltip: 'Egresos Pendientes'        },
                    { title: 'Agrupadores',          url: CONTABILIDAD_ROUTES.AGRUPADORES,        icon: FolderTree,      tooltip: 'Agrupadores de egresos'    },
                    { title: 'Utilidad',             url: CONTABILIDAD_ROUTES.UTILIDAD,           icon: PieChart,        tooltip: 'Distribución de Utilidades'},
                    { title: 'Socios',               url: CONTABILIDAD_ROUTES.SOCIOS,             icon: Users,           tooltip: 'Gestión de Socios'         },
                    { title: 'Cajas',                url: CONTABILIDAD_ROUTES.CAJAS,              icon: Wallet,          tooltip: 'Gestión de Cajas'          },
                  ].map((item) => {
                    const active = isActive(item.url);
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          tooltip={item.tooltip}
                          isActive={active}
                          className={active ? activeClass : ''}
                        >
                          <Link to={item.url}>
                            <item.icon />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter>
          <NavUser user={{
            name: user?.name || 'Usuario',
            email: user?.email || '',
            avatar: user?.avatar || '',
          }} />
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center justify-between gap-2 px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-14">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <SidebarTrigger className="-ml-1 shrink-0" />
            <Separator orientation="vertical" className="mr-1 sm:mr-2 h-14 data-[orientation=vertical]:h-14" />
            {prevButtonVisible && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full size-8 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={onPrevClick}
              >
                <ArrowLeft className="size-5 text-primary-foreground" />
              </Button>
            )}
            <h1
              className="min-w-0 truncate text-md md:text-xl font-medium ml-2 sm:ml-4 text-gray-700 border-b border-red-300 text-center"
              title={title}
            >
              {title}
            </h1>
          </div>
          <div className="flex shrink-0 items-center justify-end">
            <Link to="/" className="flex-shrink-0">
              <AppLogo height={60} width={120} className="h-auto w-20 sm:w-[120px]" />
            </Link>
          </div>
        </header>
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
};

SidebarLayout.displayName = 'SidebarLayout';

export default SidebarLayout;
